import instance from "./index";
import { requireAuthToken } from "../utils/authUtils";
import {
  CLIENT_KNOWLEDGE_XLSX_ERROR,
  hasProjectKnowledgeXlsxBlockedFile,
  MEETING_NOTES_XLSX_ERROR,
  OTHER_DOCUMENTS_XLSX_ERROR,
} from "./fileUpload.service";
import {
  getThemeConfigForFile,
  normalizeBackgroundFitValue,
  parseThemeConfig,
  serializeThemeConfig,
} from "../utils/themeConfig";

const CLIENT_RESOURCE_CACHE_PREFIX = "plambo_client_resource_";
const CLIENT_RESOURCE_UPLOAD_PATH = "/clients/upload/resource";
const CLIENT_RESOURCE_DOWNLOAD_PATH = "/clients/download";

export const CLIENT_RESOURCE_NAMES = {
  KNOWLEDGE_BANK: "knowledgeBank",
  LOGO: "logo",
  THEME: "theme",
  MEETING: "meeting",
  OTHER_DOCUMENTS: "otherDocuments",
};

const getResourceCacheKey = (clientId, fileName, resourceName) =>
  `${CLIENT_RESOURCE_CACHE_PREFIX}${clientId}_${resourceName}_${encodeURIComponent(fileName)}`;

const parseFileNameList = (value) => {
  if (value == null || value === "") return [];

  if (Array.isArray(value)) {
    return value.map(String).map((name) => name.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(String).map((name) => name.trim()).filter(Boolean);
        }
      } catch {
        // Fall through to comma-separated parsing.
      }
    }
    return trimmed.split(",").map((name) => name.trim()).filter(Boolean);
  }

  return [];
};

const getClientMediaFiles = (data) => {
  const mediaFiles = data?.mediaFiles ?? data?.media_files ?? {};

  return {
    logo: parseFileNameList(mediaFiles.logo),
    theme: parseFileNameList(mediaFiles.theme),
    meeting: parseFileNameList(mediaFiles.meeting),
    knowledgeBank: parseFileNameList(
      mediaFiles.knowledgeBank ?? mediaFiles.knowledge_bank,
    ),
    otherDocuments: parseFileNameList(
      mediaFiles.otherDocuments ?? mediaFiles.other_documents,
    ),
  };
};

const getClientThemeFileNames = (data) => {
  const themeFromMedia = getClientMediaFiles(data).theme;
  if (themeFromMedia.length) return themeFromMedia;

  return parseFileNameList(data?.themeNames ?? data?.theme_names);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const clearClientResourceCache = (clientId, resourceName) => {
  if (!clientId) return;

  const prefix = resourceName
    ? `${CLIENT_RESOURCE_CACHE_PREFIX}${clientId}_${resourceName}_`
    : `${CLIENT_RESOURCE_CACHE_PREFIX}${clientId}_`;

  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    });
  } catch {
    // Ignore storage failures.
  }
};

const removeClientResourceCache = (clientId, fileNames, resourceName) => {
  if (!clientId || !resourceName) return;

  const names = (Array.isArray(fileNames) ? fileNames : [fileNames])
    .map(String)
    .filter(Boolean);

  try {
    names.forEach((fileName) => {
      localStorage.removeItem(getResourceCacheKey(clientId, fileName, resourceName));
    });
  } catch {
    // Ignore storage failures.
  }
};

const normalizeProject = (project, clientId) => ({
  ...project,
  projectId: project.projectId ?? project.Id ?? project.id ?? null,
  clientId:
    project.clientsId ??
    project.clientId ??
    project.ClientId ??
    project.client_id ??
    clientId,
  isArchived:
    project.isArchived ??
    project.IsArchived ??
    project.is_archived ??
    project.archived ??
    false,
  projectName:
    project.projectName ??
    project.Project_Name ??
    project.name ??
    "",
});

const normalizeClientList = (list) =>
  list.map((client) => {
    const clientId = client.clientId ?? client.Id ?? client.id ?? null;
    const projects = (Array.isArray(client.projects) ? client.projects : []).map(
      (project) => normalizeProject(project, clientId),
    );

    return {
      ...client,
      clientId,
      clientName: client.clientName ?? client.Client_Name ?? client.name ?? "",
      domainId:
        client.domainId ??
        client.domain_id ??
        client.clientDomainId ??
        client.DomainId ??
        null,
      domainName:
        client.domainName ??
        client.DomainName ??
        client.domain_name ??
        "",
      projects,
      themeConfig: parseThemeConfig(client.themeConfig ?? client.theme_config),
    };
  });

export const getAllClientActivate = async () => {
  const res = await instance.get("/clients/getAllClientActivate");
  const data = res?.data;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  return normalizeClientList(list);
};

export const getClientsByUserName = async (userName) => {
  const trimmedUserName = String(userName ?? "").trim();
  if (!trimmedUserName) return [];

  const token = requireAuthToken();
  const res = await instance.get("/clients/getClientsByUserName", {
    params: { userName: trimmedUserName },
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = res?.data;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  return normalizeClientList(list);
};

export const getClientById = async (clientId) => {
  if (!clientId) return null;

  const res = await instance.get(`/clients/${clientId}`);
  const data = res?.data?.data ?? res?.data;

  if (!data || typeof data !== "object") return null;

  return {
    ...data,
    clientId: data.clientId ?? data.Id ?? data.id ?? clientId,
    clientName: data.clientName ?? data.Client_Name ?? data.name ?? "",
    mediaFiles: getClientMediaFiles(data),
    themeNames: getClientThemeFileNames(data),
    themeConfig: parseThemeConfig(data?.themeConfig ?? data?.theme_config),
  };
};

export const getClientResource = async (clientId, fileName, resourceName) => {
  if (!clientId || !fileName || !resourceName) return null;

  const res = await instance.get(
    `/clients/view/${clientId}/${encodeURIComponent(fileName)}/${encodeURIComponent(resourceName)}`,
    { responseType: "blob" },
  );

  const blob = res?.data;
  if (!(blob instanceof Blob) || blob.size === 0) return null;

  return blob;
};

export const getClientResourceUrl = async (clientId, fileName, resourceName) => {
  if (!clientId || !fileName || !resourceName) return null;

  const cacheKey = getResourceCacheKey(clientId, fileName, resourceName);

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch {
    // Ignore storage read failures.
  }

  try {
    const blob = await getClientResource(clientId, fileName, resourceName);
    if (!blob) return null;

    const dataUrl = await blobToDataUrl(blob);

    try {
      localStorage.setItem(cacheKey, dataUrl);
    } catch {
      // Ignore storage write failures (quota/private mode).
    }

    return dataUrl;
  } catch {
    return null;
  }
};

const parseContentDispositionFileName = (header) => {
  if (!header || typeof header !== "string") return "";

  const encoded = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim().replace(/^"|"$/g, ""));
    } catch {
      // Fall through to the plain filename parameter.
    }
  }

  return header.match(/filename="?([^";]+)"?/i)?.[1]?.trim() ?? "";
};

/** GET /clients/download/:clientId/:fileName/:resourceName — returns the raw file. */
export const downloadClientResource = async (clientId, fileName, resourceName) => {
  if (!clientId || !fileName || !resourceName) return null;

  const res = await instance.get(
    `${CLIENT_RESOURCE_DOWNLOAD_PATH}/${clientId}/${encodeURIComponent(fileName)}/${encodeURIComponent(resourceName)}`,
    { responseType: "blob" },
  );

  const blob = res?.data;
  if (!(blob instanceof Blob) || blob.size === 0) return null;

  const headers = res?.headers ?? {};
  const disposition =
    headers["content-disposition"] ?? headers["Content-Disposition"];

  return {
    blob,
    fileName: parseContentDispositionFileName(disposition) || String(fileName),
  };
};

export const saveBlobAsFile = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

/** Fetch a client resource and save it to the user's device. */
export const downloadClientResourceFile = async (
  clientId,
  fileName,
  resourceName,
) => {
  const result = await downloadClientResource(clientId, fileName, resourceName);
  if (!result) throw new Error("File is not available for download");

  saveBlobAsFile(result.blob, result.fileName);
  return result.fileName;
};

export const getClientThemeImage = async (clientId, fileName, { resourceName = "theme" } = {}) =>
  getClientResource(clientId, fileName, resourceName);

export const getClientThemeImageUrl = async (clientId, fileName, { resourceName = "theme" } = {}) =>
  getClientResourceUrl(clientId, fileName, resourceName);

export const getClientLogo = async (clientId, fileName) =>
  getClientResource(clientId, fileName, "logo");

export const getClientLogoUrl = async (clientId, fileName) =>
  getClientResourceUrl(clientId, fileName, "logo");

const normalizeClientResourceResponse = (data, clientId) => ({
  ...data,
  clientId: data?.clientId ?? data?.Id ?? data?.id ?? clientId,
  clientName: data?.clientName ?? data?.Client_Name ?? data?.name ?? "",
  mediaFiles: getClientMediaFiles(data),
  themeNames: getClientThemeFileNames(data),
  themeConfig: parseThemeConfig(data?.themeConfig ?? data?.theme_config),
});

const postClientResourceUpload = async (
  clientId,
  files,
  resourceName,
  requestConfig = {},
) => {
  const { themeConfig, ...axiosConfig } = requestConfig;
  const formData = new FormData();
  formData.append("clientId", String(clientId));
  formData.append("resourceName", String(resourceName));
  files.forEach((file) => {
    formData.append("file", file, file.name);
  });
  if (themeConfig != null) {
    formData.append(
      "themeConfig",
      serializeThemeConfig(parseThemeConfig(themeConfig)),
    );
  }

  const res = await instance.post(CLIENT_RESOURCE_UPLOAD_PATH, formData, axiosConfig);
  const data = res?.data?.data ?? res?.data;

  clearClientResourceCache(clientId, resourceName);

  return normalizeClientResourceResponse(data, clientId);
};

export const uploadClientResource = async (
  clientId,
  file,
  { resourceName, ...requestConfig } = {},
) => {
  if (!clientId) throw new Error("Client id is required");
  if (!file) throw new Error("File is required");
  if (!resourceName) throw new Error("resourceName is required");

  return postClientResourceUpload(clientId, [file], resourceName, requestConfig);
};

export const uploadClientResources = async (
  clientId,
  files,
  { resourceName, ...requestConfig } = {},
) => {
  const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
  if (!list.length) throw new Error("At least one file is required");
  if (!clientId) throw new Error("Client id is required");
  if (!resourceName) throw new Error("resourceName is required");

  return postClientResourceUpload(clientId, list, resourceName, requestConfig);
};

export const uploadClientTheme = async (
  clientId,
  file,
  { resourceName = CLIENT_RESOURCE_NAMES.THEME, ...requestConfig } = {},
) => uploadClientResource(clientId, file, { resourceName, ...requestConfig });

export const uploadClientThemes = async (
  clientId,
  files,
  { resourceName = CLIENT_RESOURCE_NAMES.THEME, ...requestConfig } = {},
) => uploadClientResources(clientId, files, { resourceName, ...requestConfig });

export const getClientBackgroundSlides = async (clientId) => {
  if (!clientId) return null;

  const client = await getClientById(clientId);
  const themeNames = getClientThemeFileNames(client);
  const themeConfig = parseThemeConfig(client?.themeConfig);

  if (!themeNames.length) return null;

  const slides = await Promise.all(
    themeNames.map(async (themeName) => {
      const url = await getClientThemeImageUrl(clientId, themeName);
      if (!url) return null;

      const config = getThemeConfigForFile(themeConfig, themeName);
      return {
        url,
        fileName: themeName,
        backgroundFit: normalizeBackgroundFitValue(config.BackgroundFit),
        overlay: Number(config.Overlay) || 0,
      };
    }),
  );

  const validSlides = slides.filter(Boolean);
  return validSlides.length ? validSlides : null;
};

export const getAllClientDomains = async () => {
  const res = await instance.get("/clientDomain/viewAll");
  const data = res?.data;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  return list.map((domain) => ({
    ...domain,
    domainId:
      domain.domainId ??
      domain.clientDomainId ??
      domain.id ??
      domain.Id ??
      null,
    domainName: domain.domainName ?? domain.name ?? "",
  }));
};

export const createClientDomain = async (domainName) => {
  const trimmed = String(domainName ?? "").trim();
  if (!trimmed) throw new Error("Domain name is required");

  const res = await instance.post("/clientDomain", { domainName: trimmed });
  const data = res?.data?.data ?? res?.data ?? {};

  return {
    ...data,
    domainId:
      data.domainId ??
      data.clientDomainId ??
      data.id ??
      data.Id ??
      null,
    domainName: data.domainName ?? data.name ?? trimmed,
  };
};

const toFileList = (value) =>
  (Array.isArray(value) ? value : [value]).filter(Boolean);

const appendFiles = (formData, fieldName, files) => {
  toFileList(files).forEach((file) => {
    formData.append(fieldName, file, file.name);
  });
};

const findDomainByName = async (domainName) => {
  const trimmed = String(domainName ?? "").trim();
  if (!trimmed) return null;

  const domains = await getAllClientDomains();
  return (
    domains.find(
      (d) =>
        String(d.domainName ?? "").toLowerCase() === trimmed.toLowerCase(),
    ) ?? null
  );
};

const normalizeClientPayload = async (
  {
    clientName,
    domainName,
    domainId,
    knowledgeBank,
    logo,
    theme,
    themeConfig,
    meeting,
    otherDocuments,
    users,
  } = {},
  { createMissingDomain = true } = {},
) => {
  const trimmedName = String(clientName ?? "").trim();
  if (!trimmedName) throw new Error("Client name is required");

  const trimmedDomain = String(domainName ?? "").trim();
  let resolvedDomainId = domainId ?? null;

  // Reuse an existing domain (e.g. edit client without changing domain)
  // instead of always POSTing /clientDomain, which returns 409 DUPLICATE_DATA.
  if (resolvedDomainId == null && trimmedDomain) {
    const existing = await findDomainByName(trimmedDomain);
    if (existing?.domainId != null) {
      resolvedDomainId = existing.domainId;
    } else if (createMissingDomain) {
      const domain = await createClientDomain(trimmedDomain);
      resolvedDomainId = domain.domainId;
    }
  }

  return {
    clientName: trimmedName,
    domainName: trimmedDomain,
    domainId: resolvedDomainId,
    knowledgeBank,
    logo,
    theme,
    themeConfig: parseThemeConfig(themeConfig),
    meeting,
    otherDocuments,
    users,
  };
};

const buildClientFormData = ({
  clientName,
  domainName,
  domainId,
  knowledgeBank,
  logo,
  theme,
  themeConfig,
  meeting,
  otherDocuments,
  users,
  includeDomainId = true,
} = {}) => {
  const formData = new FormData();
  formData.append("clientName", clientName);
  formData.append("domainName", domainName);

  if (includeDomainId && domainId != null) {
    formData.append("domainId", String(domainId));
  }

  appendFiles(formData, "knowledgeBank", knowledgeBank);
  appendFiles(formData, "logo", logo);
  appendFiles(formData, "theme", theme);
  appendFiles(formData, "meeting", meeting);
  appendFiles(formData, "otherDocuments", otherDocuments);
  formData.append("themeConfig", serializeThemeConfig(parseThemeConfig(themeConfig)));

  if (users && typeof users === "object" && Object.keys(users).length > 0) {
    formData.append("users", JSON.stringify(users));
  }

  return formData;
};

const normalizeClientMutationResponse = (data, fallback = {}) => {
  const clientId =
    data?.clientId ?? data?.Id ?? data?.id ?? fallback.clientId ?? null;

  return {
    ...data,
    clientId,
    clientName:
      data?.clientName ??
      data?.Client_Name ??
      data?.name ??
      fallback.clientName ??
      "",
    mediaFiles: getClientMediaFiles(data),
    themeConfig: parseThemeConfig(data?.themeConfig ?? data?.theme_config),
  };
};

export const createClient = async (payload = {}, requestConfig = {}) => {
  const normalized = await normalizeClientPayload(payload);

  if (hasProjectKnowledgeXlsxBlockedFile(normalized.knowledgeBank)) {
    throw new Error(CLIENT_KNOWLEDGE_XLSX_ERROR);
  }
  if (hasProjectKnowledgeXlsxBlockedFile(normalized.meeting)) {
    throw new Error(MEETING_NOTES_XLSX_ERROR);
  }
  if (hasProjectKnowledgeXlsxBlockedFile(normalized.otherDocuments)) {
    throw new Error(OTHER_DOCUMENTS_XLSX_ERROR);
  }

  const formData = buildClientFormData(normalized);
  const res = await instance.post("/clients", formData, requestConfig);
  const data = res?.data?.data ?? res?.data ?? {};

  return normalizeClientMutationResponse(data, {
    clientName: normalized.clientName,
  });
};

/** PUT /clients/update/:id — multipart form-data matching create fields. */
export const updateClient = async (
  { clientId, ...payload } = {},
  requestConfig = {},
) => {
  const id = clientId ?? payload.id ?? payload.Id ?? null;
  if (id == null || String(id).trim() === "") {
    throw new Error("Client id is required");
  }

  // Do not create domains on update — unchanged domains already exist and
  // POSTing them causes 409 DUPLICATE_DATA ("Domain already present").
  const normalized = await normalizeClientPayload(payload, {
    createMissingDomain: false,
  });

  if (hasProjectKnowledgeXlsxBlockedFile(normalized.knowledgeBank)) {
    throw new Error(CLIENT_KNOWLEDGE_XLSX_ERROR);
  }
  if (hasProjectKnowledgeXlsxBlockedFile(normalized.meeting)) {
    throw new Error(MEETING_NOTES_XLSX_ERROR);
  }
  if (hasProjectKnowledgeXlsxBlockedFile(normalized.otherDocuments)) {
    throw new Error(OTHER_DOCUMENTS_XLSX_ERROR);
  }

  // Update API expects the same form fields as Postman (no domainId).
  const formData = buildClientFormData({
    ...normalized,
    includeDomainId: false,
  });

  const res = await instance.put(
    `/clients/update/${id}`,
    formData,
    requestConfig,
  );
  const data = res?.data?.data ?? res?.data ?? {};

  return normalizeClientMutationResponse(data, {
    clientId: id,
    clientName: normalized.clientName,
  });
};

export const getProjectByClientId = async (clientId, isArchived = false) => {
  if (!clientId) return [];

  // const res = await instance.get(`/project/getProjectByClientId/${clientId}`, {
  //   params: { isArchived },
  // });
  // const data = res?.data;
  // const list = Array.isArray(data)
  //   ? data
  //   : Array.isArray(data?.data)
  //     ? data.data
  //     : [];

  // return list.map((project) => normalizeProject(project, clientId));
  void isArchived;
  return [];
};


export const deleteClientThemes = async (clientId, themes) => {
  if (!clientId) throw new Error("Client id is required");

  const list = (Array.isArray(themes) ? themes : [themes])
    .map(String)
    .map((name) => name.trim())
    .filter(Boolean);

  if (!list.length) throw new Error("At least one theme is required");

  const res = await instance.delete("/clients/deleteThemes", {
    data: {
      clientId,
      themes: list,
    },
  });

  const data = res?.data?.data ?? res?.data ?? {};
  removeClientResourceCache(clientId, list, "theme");

  return {
    ...data,
    clientId: data.clientId ?? data.Id ?? data.id ?? clientId,
    clientName: data.clientName ?? data.Client_Name ?? data.name ?? "",
    mediaFiles: getClientMediaFiles(data),
    themeNames: getClientThemeFileNames(data),
  };
};

export const deleteThemes = deleteClientThemes;

export const deleteClientResource = async (clientId, fileNames, resourceName) => {
  if (!clientId) throw new Error("Client id is required");

  const resource = String(resourceName ?? "").trim();
  if (!resource) throw new Error("resourceName is required");

  const list = (Array.isArray(fileNames) ? fileNames : [fileNames])
    .map(String)
    .map((name) => name.trim())
    .filter(Boolean);

  if (!list.length) throw new Error("At least one file name is required");

  const res = await instance.delete("/clients/deleteResource", {
    data: {
      clientId,
      fileNames: list,
      resourceName: resource,
    },
  });

  const data = res?.data?.data ?? res?.data ?? {};
  removeClientResourceCache(clientId, list, resource);

  return {
    ...data,
    clientId: data.clientId ?? data.Id ?? data.id ?? clientId,
    clientName: data.clientName ?? data.Client_Name ?? data.name ?? "",
    mediaFiles: getClientMediaFiles(data),
    themeNames: getClientThemeFileNames(data),
  };
};